import { TimeframeWithLocalTimes } from '@ftr/contracts/api/shared'
import {
  AudioDevice,
  AudioHost,
  getConfigForDayOfWeek,
  ProducerCondition,
  ProducerState,
  StreamLocationConfiguration,
} from '@ftr/contracts/regional-api'
import { collect } from '@ftr/contracts/shared/ArrayUtils'
import { assertUnreachable } from '@ftr/contracts/shared/assertUnreachable'
import { DayOfWeek, getDayOfWeek } from '@ftr/contracts/shared/DayOfWeek'
import { SelectItem } from '@ftr/forms'
import {
  ApiResult,
  ArrayUtils,
  BadgeColors,
  BadgeDetails,
  DateFormat,
  jodaFormatPatternWithLocale,
  JsJodaUtils,
} from '@ftr/foundation'
import { LaunchDarklyService } from '@ftr/ui-feature-flags'
import { Duration, LocalDate, LocalDateTime, LocalTime, ZonedDateTime, ZoneId } from '@js-joda/core'
import { map } from 'rxjs'
import semver from 'semver/preload'
import { ProducerDetailWithLocation } from '../api'
import { ProducerDetailWithLocationBuilder } from '../builders'
import { RealTimeRecorderIssueWithDetails } from '../conditions'

export const HEARTBEAT_INTERVAL_IN_MS = 15_000

export const OFFLINE_RECORDING_CAPABLE_VERSION = '1.5.22'

export enum ProducerCategory {
  Connected,
  Disconnected,
  Error,
  New,
}

export const AudioHostToDriverMapping: Record<AudioHost, string> = {
  UNSPECIFIED: 'Unspecified',
  LINUX_ALSA: 'Advanced Linux Sound Architecture (ALSA)',
  LINUX_PULSE: 'Pulse Audio',
  LINUX_OSS: 'Open Sound System (OSS)',
  UNIX_JACK: 'JACK Audio Connection Kit',
  MACOSX_CORE: 'Core Audio',
  WINDOWS_WASAPI: 'Windows Audio Session API (WASAPI)',
  WINDOWS_ASIO: 'Audio Stream Input/Output (ASIO)',
  WINDOWS_DS: 'Direct Show',
  RTAUDIO_DUMMY: 'RtAudio Dummy',
}

export enum AudioDeviceType {
  Input,
  Output,
}

export function getTestIssues(): RealTimeRecorderIssueWithDetails[] {
  return getIssues(generateTestData())
}

export function getTestConditions(): ProducerCondition[] {
  return getTestIssues().map(x => x.condition)
}

export function getTestRecordingConditions(): ProducerCondition[] {
  return [
    new ProducerDetailWithLocationBuilder().starting().mismatchTimezone().build(),
    new ProducerDetailWithLocationBuilder().recording().clockNotInSync().build(),
  ].flatMap(x => x.conditions ?? [])
}

// Test data with every state
export function generateTestData(): ProducerDetailWithLocation[] {
  return [
    new ProducerDetailWithLocationBuilder().recording().build(),
    new ProducerDetailWithLocationBuilder().new().build(),
    new ProducerDetailWithLocationBuilder().error().noAudioDevice().build(),
    new ProducerDetailWithLocationBuilder().error().noAudioDevice().clockNotInSync().missingVideoDevice().build(),
    new ProducerDetailWithLocationBuilder().notRecording().clockNotInSync().build(),
    new ProducerDetailWithLocationBuilder().notRecording().build(),
    new ProducerDetailWithLocationBuilder().disconnected().notHeartbeating().build(),
    new ProducerDetailWithLocationBuilder().starting().mismatchTimezone().build(),
    new ProducerDetailWithLocationBuilder().notInSession().missingVideoDevice().build(),
    new ProducerDetailWithLocationBuilder().configuring().build(),
    new ProducerDetailWithLocationBuilder().crashLoop().build(),
    new ProducerDetailWithLocationBuilder().recording().build(),
    new ProducerDetailWithLocationBuilder().recording().build(),
    new ProducerDetailWithLocationBuilder().recording().clockNotInSync().build(),
    new ProducerDetailWithLocationBuilder().streamingServerError().off().build(),
  ]
}

export function getIssues(producers: ProducerDetailWithLocation[]): RealTimeRecorderIssueWithDetails[] {
  return producers.flatMap(producer => getIssuesForProducer(producer))
}

function getIssuesForProducer(producer: ProducerDetailWithLocation): RealTimeRecorderIssueWithDetails[] {
  const conditions = producer.conditions ?? []
  return conditions.map(condition => ({
    producerId: producer.id,
    condition,
    state: producer.state,
    ip: producer.ip,
    host: producer.hostname,
    courthouse: producer.courthouseName,
    courtroom: producer.courtroomName,
    location: producer.location,
    recordingId: producer.recordingId,
  }))
}

export function getBadgeDetailsForState(state: ProducerState): BadgeDetails {
  switch (state) {
    case ProducerState.Disconnected:
      return {
        label: 'Disconnected',
        icon: 'LinkOff',
        color: BadgeColors.LightGrey,
      }
    case ProducerState.NotRecording:
      return {
        label: 'Inactive',
        icon: 'Inactive',
        color: BadgeColors.LightGrey,
      }
    case ProducerState.Configuring:
      return {
        label: 'Configuring',
        icon: 'InProgress',
        color: BadgeColors.Blueberry,
      }
    case ProducerState.Error:
      return {
        label: 'Error',
        icon: 'ErrorCircleSolid',
        color: BadgeColors.Brand,
      }
    case ProducerState.New:
      return {
        label: 'New',
        icon: 'AddCircle',
        color: BadgeColors.Grape,
      }
    case ProducerState.InSession:
      return {
        label: 'In Session',
        icon: 'Gavel',
        color: BadgeColors.Apple,
      }
    case ProducerState.NotInSession:
      return {
        label: 'Not In Session',
        icon: 'NotInSession',
        color: BadgeColors.Lemon,
      }
    case ProducerState.Starting:
      return {
        label: 'Starting',
        icon: 'AwaitingFulfillment',
        color: BadgeColors.Mint,
      }
    case ProducerState.Disabled:
      return {
        label: 'Disabled',
        icon: 'NoAccess',
        color: BadgeColors.Brand,
      }
    default:
      assertUnreachable(state)
  }
}

export function getCategoryForState(state: ProducerState): ProducerCategory {
  switch (state) {
    case ProducerState.Disconnected:
      return ProducerCategory.Disconnected
    case ProducerState.Error:
      return ProducerCategory.Error
    case ProducerState.New:
      return ProducerCategory.New
    case ProducerState.InSession:
    case ProducerState.NotInSession:
    case ProducerState.Starting:
    case ProducerState.NotRecording:
    case ProducerState.Disabled:
    case ProducerState.Configuring:
      return ProducerCategory.Connected
    default:
      assertUnreachable(state)
  }
}

export function convertToSelectItem(audioDevice: AudioDevice, type: AudioDeviceType): SelectItem<AudioDevice> {
  return {
    key: formatAudioDevice(audioDevice, type),
    value: audioDevice,
  }
}

export function calculateDefaultAudioDevice(
  producer: ProducerDetailWithLocation,
  existingValue?: AudioDevice | null,
): AudioDevice | null {
  if (producer.availableAudioDevices.length === 0) {
    return null
  }

  const getAvailableAudioDevice = (audioDevice: AudioDevice | null | undefined): AudioDevice | null =>
    audioDevice && isAudioDeviceAvailable(producer, audioDevice)
      ? producer.availableAudioDevices.find(x => equalsAudioDevice(x, audioDevice, AudioDeviceType.Input)) || null
      : null

  return (
    getAvailableAudioDevice(existingValue) ||
    getAvailableAudioDevice(producer.configuredAudioDevice) ||
    getAudioDeviceWithTheMostChannels(producer)
  )
}

export function calculateDefaultOutputAudioDevice(
  producer: ProducerDetailWithLocation,
  existingValue?: AudioDevice | null,
): AudioDevice | null {
  if (producer.availableOutputAudioDevices?.length === 0) {
    return null
  }

  const getAvailableOutputAudioDevice = (audioDevice: AudioDevice | null | undefined): AudioDevice | null =>
    audioDevice && isOutputAudioDeviceAvailable(producer, audioDevice)
      ? producer.availableOutputAudioDevices?.find(x => equalsAudioDevice(x, audioDevice, AudioDeviceType.Output)) ||
        null
      : null

  return (
    getAvailableOutputAudioDevice(producer.configuredOutputAudioDevice) || getAvailableOutputAudioDevice(existingValue)
  )
}

// If the producer state is error, our audio device is listed, but unavailable
function isAudioDeviceAvailable(producer: ProducerDetailWithLocation, audioDevice: AudioDevice): boolean {
  return producer.availableAudioDevices.some(device => equalsAudioDevice(device, audioDevice, AudioDeviceType.Input))
}

function isOutputAudioDeviceAvailable(producer: ProducerDetailWithLocation, audioDevice: AudioDevice): boolean {
  if (!producer.availableOutputAudioDevices) return false
  return producer.availableOutputAudioDevices.some(device =>
    equalsAudioDevice(device, audioDevice, AudioDeviceType.Output),
  )
}

export function getAudioDeviceWithTheMostChannels(producer: ProducerDetailWithLocation): AudioDevice {
  // Note: Streaming currently only allows up to 8 channels, but there is no max here, so this allows an invalid config.
  return producer.availableAudioDevices.sort((a, b) => b.inputs - a.inputs)[0]
}

export function equalsAudioDevice(a: AudioDevice, b: AudioDevice, type: AudioDeviceType): boolean {
  const nameEquals = a.name === b.name
  const hostEquals = a.host === b.host
  const channelsEqual = type === AudioDeviceType.Input ? a.inputs === b.inputs : a.outputs === b.outputs

  return nameEquals && hostEquals && channelsEqual
}

/**
 * Same as {@link equalsAudioDevice} but safe when either side is null or undefined.
 * Used by ftr-select and form value comparisons so empty selection matches consistently.
 */
export function equalsAudioDeviceNullable(
  a: AudioDevice | undefined | null,
  b: AudioDevice | undefined | null,
  type: AudioDeviceType,
): boolean {
  if (a && b) {
    return equalsAudioDevice(a, b, type)
  }
  return a === b
}

export function formatAudioDevice(value: AudioDevice, type: AudioDeviceType): string {
  const inputOrOutputChannelCount = type === AudioDeviceType.Input ? value.inputs : value.outputs
  return inputOrOutputChannelCount > 0 ? `${value.name} (${inputOrOutputChannelCount} channels)` : `${value.name}`
}

/**
 * Sentinel option used in the output-audio-device select to represent
 * "do not emit audio". Detected on the way back to the API by
 * {@link isNoOutputDevice} so the producer config posts `undefined` instead
 * of this fake device.
 *
 * Keep the constant + predicate co-located so renames stay in sync.
 */
export const NO_OUTPUT_DEVICE_SENTINEL: AudioDevice = {
  name: 'No Output Device',
  outputs: 0,
  inputs: 0,
  host: AudioHost.Unspecified,
}

export function isNoOutputDevice(device: AudioDevice | null | undefined): boolean {
  return device?.name === NO_OUTPUT_DEVICE_SENTINEL.name
}

/**
 * Bound predicates suitable for `[equals]` on `<ftr-select>`. Avoids the
 * three-way duplication of inline arrow wrappers across the audio components.
 */
export const audioInputDeviceEquals = (a: AudioDevice | undefined | null, b: AudioDevice | undefined | null): boolean =>
  equalsAudioDeviceNullable(a, b, AudioDeviceType.Input)

export const audioOutputDeviceEquals = (
  a: AudioDevice | undefined | null,
  b: AudioDevice | undefined | null,
): boolean => equalsAudioDeviceNullable(a, b, AudioDeviceType.Output)

export interface AudioDeviceSelectModel {
  items: SelectItem<AudioDevice>[]
  defaultValue: AudioDevice | null
}

/**
 * Build the input-device dropdown options + the default value to seed the
 * form with. `existingValue` lets callers preserve an in-progress user
 * selection when the producer is reloaded.
 */
export function buildAudioInputDeviceOptions(
  producer: ProducerDetailWithLocation,
  existingValue?: AudioDevice | null,
): AudioDeviceSelectModel {
  return {
    items: producer.availableAudioDevices.map(device => convertToSelectItem(device, AudioDeviceType.Input)),
    defaultValue: calculateDefaultAudioDevice(producer, existingValue),
  }
}

/**
 * Build the output-device dropdown options + the default value. Always
 * prepends the {@link NO_OUTPUT_DEVICE_SENTINEL} so the user can opt out of
 * audio emission even when the producer reports devices.
 */
export function buildAudioOutputDeviceOptions(
  producer: ProducerDetailWithLocation,
  existingValue?: AudioDevice | null,
): AudioDeviceSelectModel {
  const devices = producer.availableOutputAudioDevices
    ? [NO_OUTPUT_DEVICE_SENTINEL, ...producer.availableOutputAudioDevices]
    : [NO_OUTPUT_DEVICE_SENTINEL]
  return {
    items: devices.map(device => convertToSelectItem(device, AudioDeviceType.Output)),
    defaultValue: calculateDefaultOutputAudioDevice(producer, existingValue),
  }
}

/**
 * Single source of truth for the `rth-defined-channel-audio-emission` LD
 * flag. The flag is a number where 0 means disabled and any positive value
 * enables hybrid recorder audio emission.
 */
export function observeAudioEmissionEnabled(launchDarklyService: LaunchDarklyService): ApiResult<boolean> {
  return ApiResult.from(
    launchDarklyService.observeFlagChanges<number>('rth-defined-channel-audio-emission').pipe(map(value => value > 0)),
  )
}

export function isScheduledRecorder(recorder: ProducerDetailWithLocation): boolean {
  return getCountOfDaysScheduled(recorder) > 0
}

export function getNextScheduledStartTime(recorder: ProducerDetailWithLocation): LocalDateTime | undefined {
  if (!isScheduledRecorder(recorder)) {
    return undefined
  }

  if (isCurrentlyBeforeTodaysScheduledStartTime(recorder)) {
    const dateOfToday = nowInRecorderTimezone(recorder).toLocalDate()
    const nextScheduledStartTime = getTodaysLocationConfigurationFromServerTime(recorder)?.schedule?.start
    return nextScheduledStartTime?.atDate(dateOfToday)
  }

  const nextScheduledDay = getNextScheduledDayOfTheWeek(recorder)

  const dateOfNextDay = getDateOfNextDay(recorder, nextScheduledDay)

  const nextScheduledStartTime = getConfigForDayOfWeek(recorder.weeklyStreamLocationConfiguration!, nextScheduledDay)
    .schedule?.start
  return nextScheduledStartTime?.atDate(dateOfNextDay)
}

/**
 * IMPORTANT: Does not include today, if the day of week is todays day of week, this will return next week
 */
export function getDateOfNextDay(recorder: ProducerDetailWithLocation, day: DayOfWeek): LocalDate {
  const now = nowInRecorderTimezone(recorder)
  const currentDayOfWeek = getDayOfWeek(now)
  const days = Object.values(DayOfWeek)
  const sortedDays = ArrayUtils.rotateTo(days, currentDayOfWeek)
  // But of a trick, the index of the next scheduled day of the week is the number of days to add to get to it
  // This works because we rotate the current day of the week to the start of the array
  const daysUntilNextScheduledDay = sortedDays.indexOf(day)
  if (currentDayOfWeek === day) {
    if (isCurrentlyBeforeTodaysScheduledStartTime(recorder)) {
      return now.toLocalDate()
    }
    return now.plusDays(7).toLocalDate()
  }
  return now.plusDays(daysUntilNextScheduledDay).toLocalDate()
}

export function isCurrentlyBeforeTodaysScheduledStartTime(recorder: ProducerDetailWithLocation): boolean {
  const now = nowInRecorderTimezone(recorder).toLocalTime()
  const todaysConfig = getTodaysLocationConfigurationFromServerTime(recorder)
  if (!todaysConfig?.schedule?.start) {
    return false
  }
  return now.isBefore(todaysConfig.schedule.start)
}

export function isCurrentlyAfterTodaysScheduledEndTime(recorder: ProducerDetailWithLocation): boolean {
  const now = nowInRecorderTimezone(recorder).toLocalTime()
  const todaysConfig = getTodaysLocationConfigurationFromServerTime(recorder)
  if (!todaysConfig?.schedule?.end) {
    return false
  }
  return now.isAfter(todaysConfig.schedule.end)
}

export function getNextScheduledDayOfTheWeek(recorder: ProducerDetailWithLocation): DayOfWeek {
  const currentDayOfTheWeek = getRecorderDayOfWeek(recorder)

  const scheduledDays = getScheduleDaysOfTheWeek(recorder)

  if (scheduledDays.length === 1) {
    return scheduledDays[0]
  }

  const allDays = Object.values(DayOfWeek)
  const daysStartingOnToday = ArrayUtils.rotateTo(allDays, currentDayOfTheWeek)
  for (const day of daysStartingOnToday) {
    if (scheduledDays.includes(day)) {
      if (day === currentDayOfTheWeek && !isCurrentlyBeforeTodaysScheduledStartTime(recorder)) {
        continue
      }
      return day
    }
  }
  return currentDayOfTheWeek
}

export function getScheduleDaysOfTheWeek(recorder: ProducerDetailWithLocation): DayOfWeek[] {
  if (!isScheduledRecorder(recorder)) {
    return []
  }
  const config = recorder.weeklyStreamLocationConfiguration!
  return Object.values(DayOfWeek).filter(day => getConfigForDayOfWeek(config, day).schedule)
}

/**
 * Provided the recorder is currently recording, this will return when its scheduled to stop recording
 */
export function getScheduledEndTime(recorder: ProducerDetailWithLocation): LocalTime | undefined {
  const config = getTodaysLocationConfigurationFromServerTime(recorder)
  if (isCurrentlyAfterTodaysScheduledEndTime(recorder)) {
    return undefined
  }
  return config?.schedule?.end
}

export function getCurrentSessionEndTime(recorder: ProducerDetailWithLocation): LocalTime | undefined {
  return getCurrentSession(recorder)?.end
}

export function getNextSessionStartTime(recorder: ProducerDetailWithLocation): LocalTime | undefined {
  return getNextSession(recorder)?.start
}

export function getNextSession(recorder: ProducerDetailWithLocation): TimeframeWithLocalTimes | undefined {
  const config = getTodaysLocationConfigurationFromServerTime(recorder)
  if (config?.onTheRecordSchedules) {
    return getUpcomingSessions(recorder)[0]
  }

  return undefined
}

export function getUpcomingSessions(recorder: ProducerDetailWithLocation): TimeframeWithLocalTimes[] {
  const config = getTodaysLocationConfigurationFromServerTime(recorder)
  if (config?.onTheRecordSchedules) {
    const now = nowInRecorderTimezone(recorder)
    const filtered = config.onTheRecordSchedules.filter(session => session.start.isAfter(now.toLocalTime()))
    return filtered.sort((a, b) => a.start.compareTo(b.start))
  }
  return []
}

export function getCurrentSession(recorder: ProducerDetailWithLocation): TimeframeWithLocalTimes | undefined {
  const config = getTodaysLocationConfigurationFromServerTime(recorder)
  if (config?.onTheRecordSchedules) {
    const now = nowInRecorderTimezone(recorder)
    for (const session of config.onTheRecordSchedules) {
      const isCurrentSession = JsJodaUtils.isDuring(
        session.start.atDate(now.toLocalDate()),
        session.end.atDate(now.toLocalDate()),
        now,
      )
      if (isCurrentSession) {
        return session
      }
    }
  }

  return undefined
}

export function getTodaysLocationConfigurationFromServerTime(
  recorder: ProducerDetailWithLocation,
): StreamLocationConfiguration | undefined {
  const dayOfWeek = getRecorderDayOfWeek(recorder)

  return recorder.weeklyStreamLocationConfiguration && dayOfWeek
    ? getConfigForDayOfWeek(recorder.weeklyStreamLocationConfiguration, dayOfWeek)
    : undefined
}

/**
 * Gets the current day of the week in the recorders timezone
 * @param recorder
 */
export function getRecorderDayOfWeek(recorder: ProducerDetailWithLocation): DayOfWeek {
  const now = nowInRecorderTimezone(recorder)
  return getDayOfWeek(now)
}

function nowInRecorderTimezone(recorder: ProducerDetailWithLocation): LocalDateTime {
  if (typeof recorder.timezone === 'string') {
    return LocalDateTime.now(ZoneId.of(recorder.timezone))
  }
  return LocalDateTime.now(recorder.timezone)
}

export function isStreaming(recorder: ProducerDetailWithLocation): boolean {
  switch (recorder.state) {
    case ProducerState.InSession:
    case ProducerState.NotInSession:
      return true
    case ProducerState.Disconnected:
    case ProducerState.Error:
    case ProducerState.New:
    case ProducerState.Starting:
    case ProducerState.Configuring:
    case ProducerState.Disabled:
    case ProducerState.NotRecording:
    default:
      return false
  }
}

export function getCountOfDaysScheduled(recorder: ProducerDetailWithLocation): number {
  if (!recorder.weeklyStreamLocationConfiguration) {
    return 0
  }
  const schedules = collect(Object.values(recorder.weeklyStreamLocationConfiguration), x => x.schedule)
  return schedules.length
}

export type UpgradeStatus = 'Upgrading' | 'Pending' | 'Waiting for Approval' | 'Up To Date' | 'End of life'

export function getUpgradeStatus(
  recorder: ProducerDetailWithLocation,
  isAutomaticUpgradesEnabled: boolean,
  lastApprovedVersion: string,
): UpgradeStatus | undefined {
  if (recorder.state === ProducerState.Disconnected) {
    return undefined
  }

  if (recorder.upgradeVersionRange) {
    if (isAutomaticUpgradesEnabled || recorder.upgradeVersionRange.expected === lastApprovedVersion) {
      if (recorder.state === ProducerState.NotRecording || recorder.state === ProducerState.Disabled) {
        return 'Upgrading'
      }

      return 'Pending'
    }

    return 'Waiting for Approval'
  }

  return 'Up To Date'
}

export function getUpgradeStatusEolFeature(
  recorder: ProducerDetailWithLocation,
  isAutomaticUpgradesEnabled: boolean,
  lastApprovedVersion: string,
  isEndOfLife: boolean,
): UpgradeStatus {
  if (isEndOfLife) {
    return 'End of life'
  }

  if (recorder.upgradeVersionRange) {
    if (isAutomaticUpgradesEnabled || recorder.upgradeVersionRange.expected === lastApprovedVersion) {
      if (recorder.state === ProducerState.NotRecording || recorder.state === ProducerState.Disabled) {
        return 'Upgrading'
      }

      return 'Pending'
    }

    return 'Waiting for Approval'
  }

  return 'Up To Date'
}

export function isLocalRecordingSupported(recorder: ProducerDetailWithLocation): boolean {
  return isScheduledRecorder(recorder) && isRecorderLocalRecordingCapableVersion(recorder)
}

export function isRecorderLocalRecordingCapableVersion(recorder: ProducerDetailWithLocation): boolean {
  return semver.gte(recorder.version, OFFLINE_RECORDING_CAPABLE_VERSION)
}

export function getLastHeartbeatText(timeZoneId: ZoneId, heartbeatTime?: Date): string {
  if (heartbeatTime === undefined) {
    return 'N/A'
  }

  const seconds = 60
  const now = ZonedDateTime.now(timeZoneId)
  const heartbeat = ZonedDateTime.parse(heartbeatTime.toISOString()).withZoneSameInstant(timeZoneId)

  const durationInMinutes = Math.floor(Duration.between(heartbeat, now).seconds() / seconds)

  if (durationInMinutes < 1) {
    return 'Less than a minute ago'
  }

  if (durationInMinutes === 1) {
    return 'a minute ago'
  }

  if (durationInMinutes > 1 && durationInMinutes < 45) {
    return `${durationInMinutes} minutes ago`
  }

  if (durationInMinutes >= 45 && durationInMinutes < 60) {
    return 'an hour ago'
  }

  return heartbeat.format(jodaFormatPatternWithLocale(DateFormat.DateAndTime))
}
